import { Repeat } from "lucide-react";

/**
 * StandingBadge — visually flags a Gingr "Standing Reservation" (recurring booking).
 * Use anywhere a per-dog/per-row data display shows a reservation so staff can
 * quickly tell recurring guests apart from one-off bookings.
 *
 * Props:
 *   - isStanding: boolean (renders nothing when false/missing)
 *   - size: "sm" (default) | "xs" — controls padding/text size
 *   - className: extra tailwind classes
 */
export function StandingBadge({ isStanding, size = "sm", className = "" }) {
  if (!isStanding) return null;
  const padding = size === "xs" ? "px-1.5 py-0.5" : "px-2 py-0.5";
  const iconSize = size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5";
  const textSize = size === "xs" ? "text-[10px]" : "text-xs";
  return (
    <span
      className={`inline-flex items-center gap-1 ${padding} rounded-full font-semibold text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 shadow-sm ${textSize} ${className}`}
      title="Standing Reservation (recurring booking)"
      data-testid="standing-badge"
    >
      <Repeat className={iconSize} />
      Standing
    </span>
  );
}

/**
 * StandingBreakdownLine — a small "Includes X standing of Y total" line for
 * surface-level summaries (bath list header, going-home header, exec dashboard).
 *
 * Props:
 *   - standing: number
 *   - total: number
 *   - label: optional context label like "guests", "baths", "dogs"
 */
export function StandingBreakdownLine({ standing, total, label = "" }) {
  if (!total || standing === undefined || standing === null) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-violet-700" data-testid="standing-breakdown">
      <Repeat className="h-3 w-3" />
      <span>
        <span className="font-semibold">{standing}</span> standing
        {label ? ` ${label}` : ""} of <span className="font-semibold">{total}</span>
      </span>
    </span>
  );
}
